import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const discDot = { artistica: "bg-[#38BDF8]", ritmica: "bg-gold" };
const discBar = { artistica: "bg-[#38BDF8]/25 hover:bg-[#38BDF8]/40", ritmica: "bg-gold/25 hover:bg-gold/40" };

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarGrid({ events, canManage, onDayClick, onEventClick }) {
  const today = new Date();
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-based
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const eventsByDate = {};
  events.forEach((ev) => {
    if (!ev.date) return;
    const end = ev.end_date && ev.end_date >= ev.date ? ev.end_date : ev.date;
    const [sy, sm, sd] = ev.date.split("-").map(Number);
    const cur = new Date(sy, sm - 1, sd);
    let guard = 0;
    while (guard < 120) {
      const key = ymd(cur);
      (eventsByDate[key] = eventsByDate[key] || []).push(ev);
      if (key === end) break;
      cur.setDate(cur.getDate() + 1);
      guard++;
    }
  });

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const go = (delta) => setView(new Date(year, month + delta, 1));
  const goYear = (delta) => setView(new Date(year + delta, month, 1));

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F172A] p-4 sm:p-6 mb-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev-year" onClick={() => goYear(-1)} className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-gold hover:bg-white/5 transition-colors text-xs font-semibold">«</button>
          <button data-testid="cal-prev-month" onClick={() => go(-1)} className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-gold hover:bg-white/5 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        </div>
        <h3 data-testid="cal-title" className="font-display text-2xl">{MONTHS[month]} <span className="text-gold">{year}</span></h3>
        <div className="flex items-center gap-2">
          <button data-testid="cal-next-month" onClick={() => go(1)} className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-gold hover:bg-white/5 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          <button data-testid="cal-next-year" onClick={() => goYear(1)} className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-gold hover:bg-white/5 transition-colors text-xs font-semibold">»</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-xs uppercase tracking-widest text-muted-foreground pb-2">{w}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[72px] sm:min-h-[92px]" />;
          const dateStr = ymd(new Date(year, month, d));
          const dayEvents = eventsByDate[dateStr] || [];
          const isToday = dateStr === ymd(today);
          return (
            <div
              key={i}
              data-testid={`cal-day-${dateStr}`}
              onClick={() => canManage && onDayClick(dateStr)}
              className={`group min-h-[72px] sm:min-h-[92px] rounded-lg border p-1.5 transition-colors ${isToday ? "border-gold/60 bg-gold/5" : "border-white/10 bg-black/20"} ${canManage ? "cursor-pointer hover:border-gold/40" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm ${isToday ? "text-gold font-semibold" : "text-muted-foreground"}`}>{d}</span>
                {canManage && <Plus className="w-3.5 h-3.5 text-transparent group-hover:text-gold transition-colors" />}
              </div>
              <div className="mt-1 space-y-1">
                {dayEvents.slice(0, 3).map((ev) => {
                  const evEnd = ev.end_date && ev.end_date >= ev.date ? ev.end_date : ev.date;
                  const col = i % 7;
                  const isStart = dateStr === ev.date;
                  const isEnd = dateStr === evEnd;
                  const showTitle = isStart || col === 0;
                  const roundL = isStart || col === 0;
                  const roundR = isEnd || col === 6;
                  return (
                    <button
                      key={ev.id}
                      data-testid={`cal-event-${ev.id}`}
                      onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                      title={ev.title}
                      className={`w-full flex items-center gap-1 text-left px-1 py-0.5 transition-colors ${discBar[ev.discipline] || "bg-white/10 hover:bg-white/20"} ${roundL ? "rounded-l-md" : "-ml-1.5 rounded-l-none"} ${roundR ? "rounded-r-md" : "-mr-1.5 rounded-r-none"}`}
                    >
                      {showTitle ? (
                        <>
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${discDot[ev.discipline] || "bg-gold"}`} />
                          <span className="text-[11px] truncate text-foreground/90">{ev.title}</span>
                        </>
                      ) : (
                        <span className="text-[11px] truncate text-transparent select-none">·</span>
                      )}
                    </button>
                  );
                })}
                {dayEvents.length > 3 && <span className="text-[10px] text-muted-foreground pl-1">+{dayEvents.length - 3} altri</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gold" /> Ritmica</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#38BDF8]" /> Artistica</span>
      </div>
    </div>
  );
}
